import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Close,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

interface Contact {
  id: string;
  full_name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization: string;
  title: string;
  notes: string;
  can_login: boolean;
  display_email: string;
  user: string | null;  // User ID if this contact is linked to a user account
}

export default function ContactsSettings() {
  const { access } = useAuth();
  const headers = { Authorization: `Bearer ${access}` };

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organization: '',
    title: '',
    notes: '',
  });

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts/', { headers });
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || []);
      setContacts(data);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setDialogMode('create');
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      organization: '',
      title: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setDialogMode('edit');
    setEditingContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone,
      organization: contact.organization,
      title: contact.title,
      notes: contact.notes,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }

    try {
      if (dialogMode === 'create') {
        await api.post('/contacts/', formData, { headers });
      } else if (editingContact) {
        await api.patch(`/contacts/${editingContact.id}/`, formData, { headers });
      }

      setDialogOpen(false);
      setEditingContact(null);
      setError('');
      await fetchContacts();
    } catch (err: any) {
      console.error('Failed to save contact:', err);
      setError(err.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDeleteClick = (contact: Contact) => {
    setContactToDelete(contact);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await api.delete(`/contacts/${contactToDelete.id}/`, { headers });
      setDeleteConfirmOpen(false);
      setContactToDelete(null);
      await fetchContacts();
    } catch (err: any) {
      console.error('Failed to delete contact:', err);
      setError(err.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columns: GridColDef<Contact>[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
          {params.row.user && (
            <Chip
              icon={<Person fontSize="small" />}
              label="User"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      minWidth: 130,
      valueGetter: (value, row) => row.phone || '-',
    },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1.5,
      minWidth: 160,
      valueGetter: (value, row) => row.organization || '-',
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 130,
      valueGetter: (value, row) => row.title || '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isUserContact = !!params.row.user;
        return (
          <Box>
            <Tooltip title={isUserContact ? "User contacts must be edited in the Users tab" : "Edit contact"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleEdit(params.row)}
                  color="primary"
                  disabled={isUserContact}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isUserContact ? "User contacts must be removed in the Users tab" : "Delete contact"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(params.row)}
                  color="error"
                  disabled={isUserContact}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Contacts</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your organization's contacts
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Add Contact
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Contacts linked to user accounts are shown with a "User" badge and must be managed in the Users tab.
      </Alert>

      <DataGrid
        rows={contacts}
        columns={columns}
        loading={loading}
        autoHeight
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#616161',
            borderBottom: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#616161',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              '& .MuiIconButton-root': {
                color: 'white',
              },
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'white',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'white',
            },
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid',
            borderTopColor: 'divider',
          },
        }}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Add Contact' : 'Edit Contact'}
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                required
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Last Name"
                required
                value={formData.last_name}
                onChange={(e) => handleFormChange('last_name', e.target.value)}
              />
            </Box>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
            />

            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
            />

            <TextField
              fullWidth
              label="Organization"
              value={formData.organization}
              onChange={(e) => handleFormChange('organization', e.target.value)}
            />

            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
            />

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm Delete
          <IconButton
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {contactToDelete && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Are you sure you want to delete this contact?
              </Alert>
              <Typography variant="body1">
                <strong>{contactToDelete.full_name}</strong>
              </Typography>
              {contactToDelete.email && (
                <Typography variant="body2" color="text.secondary">
                  {contactToDelete.email}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}